import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { queryClient } from '../lib/queryClient'
import {
  clearProfileCache,
  discardLegacyProfileCache,
  isForeignProfileQuery,
  normalizeAssetId,
} from '../features/profile/profileCache'
import { keycloak, LOGIN_OPTIONS } from './keycloak'
import { Ctx } from './authContext'

// Module-level (not a ref): keycloak.init() may run only once per page load,
// but React StrictMode double-mounts effects in dev. A ref resets per mount; a
// module flag survives the re-mount, so init() fires exactly once.
let initStarted = false

export function AuthProvider({ children }: { children: ReactNode }) {
  const [initialized, setInitialized] = useState(false)
  const [authenticated, setAuthenticated] = useState(false)

  useEffect(() => {
    if (initStarted) return
    initStarted = true

    keycloak
      .init({
        onLoad: 'check-sso', // don't force login on landing; use 'login-required' to force it
        // silently reuse an existing KC session (e.g. the one created right after a
        // user sets their password from the onboarding email) via a redirect iframe
        // to a static page — see public/silent-check-sso.html.
        silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
        pkceMethod: 'S256',
        checkLoginIframe: false, // third-party-cookie blocking breaks the iframe check
        // check-sso above is a prompt=none silent re-login. Like any silent re-login
        // it must request our custom scopes explicitly — otherwise the token restored
        // on refresh drops them (they're optional client scopes) and every scope-gated
        // BFF route (e.g. /me/asset-data → omnideck:read) 403s. Mirror LOGIN_OPTIONS.
        scope: LOGIN_OPTIONS.scope,
      })
      .then((auth) => {
        setAuthenticated(auth)
        setInitialized(true)
      })
      .catch(() => setInitialized(true))

    // keep the token fresh while the tab is open (access tokens last ~15 min)
    keycloak.onTokenExpired = () => {
      keycloak.updateToken(30).catch(() => keycloak.login(LOGIN_OPTIONS))
    }
  }, [])

  const t = keycloak.tokenParsed as Record<string, unknown> | undefined
  const signedInAssetId = authenticated ? normalizeAssetId(t?.assetId) : undefined

  // A new identity must not keep the previous user's profile query. Logout
  // clears storage; this covers a session that changes without a full reload.
  useEffect(() => {
    if (!initialized) return
    try {
      discardLegacyProfileCache()
    } catch {
      // storage unavailable — the query drop below still applies
    }
    queryClient.removeQueries({
      predicate: (query) => isForeignProfileQuery(query.queryKey, signedInAssetId),
    })
  }, [initialized, signedInAssetId])

  return (
    <Ctx.Provider
      value={{
        initialized,
        authenticated,
        claims:
          authenticated && t
            ? { email: t.email as string, name: t.name as string, assetId: signedInAssetId }
            : null,
        login: () => keycloak.login(LOGIN_OPTIONS),
        logout: () => {
          try {
            clearProfileCache()
          } catch {
            // storage unavailable — in-memory clear below still drops this session
          }
          queryClient.clear()
          void keycloak.logout({ redirectUri: window.location.origin + '/signed-out' })
        },
      }}
    >
      {children}
    </Ctx.Provider>
  )
}
